import * as v from "valibot";
import type {
  ApiSchema,
  ApiSchemaRoute,
  HttpMethod,
} from "@supply25/shared/api/schema";

/**
 * How a call reaches the network. Injected so an app reuses its existing
 * fetch stack — token refresh, workspace scope headers, credentials — and
 * this module stays free of any transport opinions. Returns the parsed
 * JSON response body.
 */
export type ApiTransport = (request: {
  method: HttpMethod;
  /** Route path with `{param}` segments already substituted. */
  path: string;
  body?: unknown;
}) => Promise<unknown>;

type ApiClientMethod<TRoute extends ApiSchemaRoute> =
  TRoute["input"] extends v.GenericSchema
    ? (args: {
        body: v.InferInput<TRoute["input"]>;
        params?: Record<string, string>;
      }) => Promise<v.InferOutput<TRoute["output"]>>
    : (args?: {
        params?: Record<string, string>;
      }) => Promise<v.InferOutput<TRoute["output"]>>;

/**
 * One method per api-schema route: the call signature comes from the
 * route's `input`, the resolved value from parsing the response with its
 * `output`. Change a schema and every call site recompiles against it.
 */
export type ApiClient<TSchema extends ApiSchema> = {
  [K in keyof TSchema]: ApiClientMethod<TSchema[K]>;
};

const substituteParams = (
  path: string,
  params: Record<string, string> | undefined,
  routeName: string,
) =>
  path.replace(/\{(\w+)\}/g, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined) {
      throw new Error(`Missing path param "${name}" calling ${routeName}.`);
    }
    return encodeURIComponent(value);
  });

/**
 * Build the typed client for an api schema. Responses are runtime-parsed
 * with each route's output schema, so a drifting backend surfaces as a
 * loud parse error, not silently wrong data downstream.
 */
export const createApiClient = <TSchema extends ApiSchema>(
  schema: TSchema,
  send: ApiTransport,
): ApiClient<TSchema> => {
  const client: Record<
    string,
    (args?: { body?: unknown; params?: Record<string, string> }) => unknown
  > = {};

  for (const key in schema) {
    const route = schema[key];
    client[key] = async (args) => {
      const response = await send({
        method: route.method,
        path: substituteParams(route.path, args?.params, key),
        body: args?.body,
      });
      return v.parse(route.output, response);
    };
  }

  // A dynamically-built mapped object is beyond what the type system can
  // verify — the per-key types above are enforced at the call sites.
  return client as ApiClient<TSchema>;
};
